import { PIXELFORMAT_RGBA8, PIXELFORMAT_RGBA16F } from 'playcanvas';

/**
 * Caps we actually read from PlayCanvas WebGL/WebGPU devices.
 * `extFloatBlend` is on the WebGL device only (undefined on WebGPU).
 */
export type SplatTargetDeviceCaps = {
    isWebGPU?: boolean;
    textureHalfFloatRenderable?: boolean;
    extFloatBlend?: unknown;
    maxColorAttachments?: number;
};

function userAgentString(): string {
    if (typeof navigator === 'undefined') return '';
    return navigator.userAgent || '';
}

/**
 * iPhone / iPad (including iPadOS that reports as Macintosh).
 * Gaussian splat pass is a 2-color MRT; Apple WebGL rejects that FBO even when
 * both attachments share a format. Points/annotations use a later single-target
 * pass, which is why they still appear on a black splat view.
 */
export function isAppleWebGLDevice(device: SplatTargetDeviceCaps | null | undefined): boolean {
    if (!device || device.isWebGPU) return false;
    const ua = userAgentString();
    if (/iPhone|iPad|iPod/.test(ua)) return true;
    try {
        return navigator.platform === 'MacIntel' && Number(navigator.maxTouchPoints) > 1;
    } catch {
        return false;
    }
}

/** Two-color splat MRT (color + overlay mask). Off on Apple WebGL. */
export function shouldUseSplatMrt(device: SplatTargetDeviceCaps | null | undefined): boolean {
    if (!device) return false;
    if (isAppleWebGLDevice(device)) return false;
    const max = Number(device.maxColorAttachments);
    if (Number.isFinite(max) && max < 2) return false;
    return true;
}

/**
 * Color format for SuperSplat splat/work attachments.
 *
 * WebGL2: always RGBA8. iOS often advertises half-float + EXT_float_blend, but
 * blending ~1M gaussians into an RGBA16F MRT still produces a black pass.
 * WebGPU keeps RGBA16F.
 */
export function resolveSplatTargetColorFormat(device: SplatTargetDeviceCaps | null | undefined): number {
    if (device?.isWebGPU) {
        return PIXELFORMAT_RGBA16F;
    }
    return PIXELFORMAT_RGBA8;
}

/** Set by camera.ts when render targets are built; read by splat material rebuild. */
let volugraphSplatMrtEnabled = false;

export function getVolugraphSplatMrtEnabled(): boolean {
    return volugraphSplatMrtEnabled;
}

export function setVolugraphSplatMrtEnabled(enabled: boolean): void {
    volugraphSplatMrtEnabled = enabled;
}
